import React, { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import RadioButtonGroup from './RadioButtonGroup';
import Button from '../controls/Button';
import { addUserConfigV2 } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import { DEFAULT_UNITS, TOAST_MESSAGES } from '../constants/global-constants';
import { ParametersType, UserConfigV2 } from '../constants/telosair-interfaces';

type UnitState = {
  possibleUnits: string[];
  selected: string;
  parameterName: ParametersType;
  displayName: string;
};

/**
 * React component for managing user unit settings.
 * @returns React.ReactElement The rendered component.
 */
export default function UserUnitSettings(): ReactElement {
  const { userConfigDataV2, setUserConfigDataV2, parameters } = useData();
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState<UnitState[]>([]);

  useEffect(() => {
    if (!loading) {
      const unitsData = parameters
        .filter((item) => !!item.possibleUnits)
        .map((item) => ({
          possibleUnits: item.possibleUnits,
          selected:
            // eslint-disable-next-line no-nested-ternary
            userConfigDataV2?.config?.unit && userConfigDataV2?.config?.unit[item.parameterName]
              ? userConfigDataV2?.config?.unit[item.parameterName]
              : DEFAULT_UNITS[item.parameterName],
          parameterName: item.parameterName,
          displayName: item.displayName
        }));
      setUnits(unitsData);
    }
  }, [userConfigDataV2, loading, parameters]);

  /**
   * Saves user unit settings to the server.
   * @returns Promise<void> A Promise that resolves when the data is saved.
   */
  const saveUserSettings = async (): Promise<void> => {
    try {
      setLoading(true);
      const obj: { [key in ParametersType]: string } = {} as { [key in ParametersType]: string };
      units.forEach((item) => {
        obj[item.parameterName] = item.selected;
      });
      const payload: UserConfigV2 = userConfigDataV2?.config
        ? { ...userConfigDataV2 }
        : { config: {}, userId: null, id: null };
      payload.config.unit = obj;
      const newData = await addUserConfigV2(payload);
      setUserConfigDataV2({ ...newData.data, config: JSON.parse(newData.data?.config || '') });
      toast.success(TOAST_MESSAGES.edited);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast.error(TOAST_MESSAGES.error);
    }
  };

  /**
   * Resets unit settings to default.
   */
  const resetToDefault = (): void => {
    const defaultData = units.map((item) => ({
      ...item,
      selected: DEFAULT_UNITS[item.parameterName]
    }));
    setUnits(defaultData);
  };

  /**
   * Handles unit change for a given unit key.
   * @param unitData - Unit data.
   */
  function handleUnitChange(unitData: UnitState): void {
    const updatedData = units.map((item) => {
      if (item.parameterName === unitData.parameterName) {
        return unitData;
      }
      return item;
    });
    setUnits([...updatedData]);
  }

  return (
    <div className="p-6 my-4 text-white text-base bg-container-500 flex flex-1 flex-col rounded">
      <div>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold">Units</h1>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <Button
              theme="secondary-gray"
              size="lg"
              name="Reset To Default"
              onClick={resetToDefault}
            />
            <Button
              theme="primary"
              size="lg"
              name="Save Changes"
              onClick={() => saveUserSettings()}
              loading={loading}
              disabled={loading}
            />
          </div>
        </div>
        <div className="h-[1px] w-full bg-gray-900 my-6" />
        {/* Two columns from md up: each cell is half as wide, so the label keeps
            a fixed share and the options sit closer together than they did in a
            single full-width column. */}
        <div className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
          {units.map((item) => (
            <div className="md:flex md:items-center" key={item.parameterName}>
              <div className="md:w-2/5 md:pr-4">{item.displayName}</div>
              <RadioButtonGroup
                avoidMarginTop
                selectedOption={item.selected}
                setSelectedOption={(unit) => handleUnitChange({ ...item, selected: unit })}
                options={item.possibleUnits}
                spacing="mr-6"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
